import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { recoveryState } from './recovery';

type AuthState = { session: Session | null; loading: boolean; recovery: boolean; recoveryError: string | null; clearRecovery: () => void };
const Ctx = createContext<AuthState>({ session: null, loading: true, recovery: false, recoveryError: null, clearRecovery: () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Initial value is captured from the landing URL before the client touched it
  // (see lib/recovery). The PASSWORD_RECOVERY event is a belt-and-braces catch
  // for the PKCE path or any missed fragment.
  const [recovery, setRecovery] = useState(recoveryState.recovery);
  const [recoveryError, setRecoveryError] = useState<string | null>(recoveryState.errorMsg);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === 'PASSWORD_RECOVERY') { setRecovery(true); setRecoveryError(null); }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return (
    <Ctx.Provider value={{ session, loading, recovery, recoveryError, clearRecovery: () => { setRecovery(false); setRecoveryError(null); } }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
export const signOut = () => supabase.auth.signOut();

/** Current user's role in their organization (own membership row, RLS-visible). */
export function useMyRole(): { role: string | null; ready: boolean } {
  const { session } = useAuth();
  const [role, setRole] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!session?.user) {
      setReady(true);
      return;
    }
    supabase
      .from('memberships')
      .select('role')
      .eq('user_id', session.user.id)
      .is('deleted_at', null)
      .then(({ data }) => {
        // prefer a scoped role if present, else the first
        const roles = (data ?? []).map((r: { role: string }) => r.role);
        setRole(roles.includes('owner_scoped') ? 'owner_scoped' : (roles[0] ?? null));
        setReady(true);
      });
  }, [session?.user?.id]);
  return { role, ready };
}
